// ── Scheduling Model ────────────────────────────────────────────────────────
// Manages: Availability settings, Event Types, Bookings, Meeting Polls
// Storage: localStorage keys prefixed with 'aidey_sched_'

// Safe coercion — returns a valid Date or null. Prevents TypeError when a string,
// null, or undefined is passed where a Date is expected.
function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

const KEYS = {
  availability: 'aidey_sched_availability',
  eventTypes: 'aidey_sched_event_types',
  bookings: 'aidey_sched_bookings',
  polls: 'aidey_sched_polls',
};

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// ── Defaults ────────────────────────────────────────────────────────────────
const DEFAULT_AVAILABILITY = {
  workingHours: {
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '09:00', end: '17:00' },
    sunday: { enabled: false, start: '09:00', end: '17:00' },
  },
  bufferBefore: 0,     // minutes before each booking
  bufferAfter: 15,    // minutes after each booking
  minNotice: 60,    // minimum notice period in minutes
  maxAdvance: 60,    // how many days in advance bookings can be made
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  blockedDates: [],    // array of ISO date strings (YYYY-MM-DD)
};

// ── Sample event types ───────────────────────────────────────────────────────
function defaultEventTypes() {
  return [
    {
      id: uid(),
      name: '30 Minute Meeting',
      description: 'A quick catch-up or intro call.',
      duration: 30,
      color: '#5352ed',
      type: 'one-on-one',
      location: 'google-meet',
      locationDetails: '',
      bookingSlug: 'quick-chat',
      isActive: true,
      maxAttendees: 1,
      requiresConfirmation: false,
      questions: [
        { id: 'q1', label: 'What would you like to discuss?', type: 'text', required: false },
      ],
      reminderMinutes: [60, 1440],
      teamMembers: [],
      price: null,
      createdAt: new Date().toISOString(),
    },
    {
      id: uid(),
      name: '60 Minute Discovery Call',
      description: 'A comprehensive discovery session.',
      duration: 60,
      color: '#27ae60',
      type: 'one-on-one',
      location: 'zoom',
      locationDetails: '',
      bookingSlug: 'discovery',
      isActive: true,
      maxAttendees: 1,
      requiresConfirmation: false,
      questions: [
        { id: 'q1', label: 'What are your main goals?', type: 'text', required: true },
        { id: 'q2', label: 'Company name', type: 'text', required: false },
      ],
      reminderMinutes: [60, 1440],
      teamMembers: [],
      price: null,
      createdAt: new Date().toISOString(),
    },
  ];
}

// ── SchedulingManager ────────────────────────────────────────────────────────
class SchedulingManager {
  constructor() {
    this.availability = this._load(KEYS.availability, DEFAULT_AVAILABILITY);
    this.eventTypes = this._load(KEYS.eventTypes, null) || defaultEventTypes();
    this.bookings = this._load(KEYS.bookings, []);
    this.polls = this._load(KEYS.polls, []);
    // Persist defaults if first run
    if (!localStorage.getItem(KEYS.eventTypes)) this._save(KEYS.eventTypes, this.eventTypes);
  }

  // ── Storage helpers ────────────────────────────────────────────────────────
  _load(key, fallback) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch { return fallback; }
  }
  _save(key, data) { localStorage.setItem(key, JSON.stringify(data)); }

  // ── Availability ───────────────────────────────────────────────────────────
  getAvailability() { return this.availability; }

  saveAvailability(updates) {
    this.availability = { ...this.availability, ...updates };
    this._save(KEYS.availability, this.availability);
    return this.availability;
  }

  isDateBlocked(dateStr) {
    return (this.availability.blockedDates || []).includes(dateStr);
  }

  toggleBlockedDate(dateStr) {
    const blocked = this.availability.blockedDates || [];
    const next = blocked.includes(dateStr)
      ? blocked.filter(d => d !== dateStr)
      : [...blocked, dateStr];
    this.saveAvailability({ blockedDates: next });
  }

  // ── Slot calculation ───────────────────────────────────────────────────────
  /** Returns array of { start: Date, end: Date } for a given date */
  getAvailableSlots(date, durationMinutes, externalTasks = []) {
    date = toDate(date);
    if (!date) return [];
    const dayName = DAYS[(date.getDay() + 6) % 7]; // Mon=0
    const dayAvail = this.availability.workingHours[dayName];
    if (!dayAvail?.enabled) return [];
    if (this.isDateBlocked(date.toISOString().slice(0, 10))) return [];

    const [sh, sm] = dayAvail.start.split(':').map(Number);
    const [eh, em] = dayAvail.end.split(':').map(Number);

    const dayStart = new Date(date); dayStart.setHours(sh, sm, 0, 0);
    const dayEnd = new Date(date); dayEnd.setHours(eh, em, 0, 0);

    const bufAfter = this.availability.bufferAfter || 0;
    const bufBefore = this.availability.bufferBefore || 0;
    const minNotice = this.availability.minNotice || 0;
    const earliestBook = new Date(Date.now() + minNotice * 60000);

    // Gather confirmed/pending bookings for this day
    const dayStr = date.toISOString().slice(0, 10);
    const dayBookings = this.bookings.filter(b =>
      b.status !== 'cancelled' && b.scheduledStart?.slice(0, 10) === dayStr
    );

    // Gather external tasks (from the main app) that are busy on this day
    const dayExternalTasks = externalTasks.filter(t => {
      if (!t.busy || t.completed) return false;
      const tDate = (t.datetime || t.date || '').slice(0, 10);
      return tDate === dayStr;
    });

    const slots = [];
    let cursor = new Date(dayStart);

    while (cursor < dayEnd) {
      const slotStart = new Date(cursor.getTime() + bufBefore * 60000);
      const slotEnd = new Date(slotStart.getTime() + durationMinutes * 60000);
      if (slotEnd > dayEnd) break;

      if (slotStart >= earliestBook) {
        const hasBookingConflict = dayBookings.some(b => {
          const bs = new Date(b.scheduledStart);
          const be = new Date(b.scheduledEnd);
          return slotStart < be && slotEnd > bs;
        });
        const hasTaskConflict = dayExternalTasks.some(t => {
          const ts = new Date(t.datetime || t.date);
          const te = t.deadline ? new Date(t.deadline) : new Date(ts.getTime() + 60 * 60000);
          return slotStart < te && slotEnd > ts;
        });
        if (!hasBookingConflict && !hasTaskConflict) {
          slots.push({ start: new Date(slotStart), end: new Date(slotEnd) });
        }
      }

      cursor = new Date(slotEnd.getTime() + bufAfter * 60000);
    }

    return slots;
  }

  /** Returns available slots for the next N days */
  getAvailableSlotsRange(durationMinutes, days = 14, externalTasks = []) {
    const result = {};
    for (let i = 0; i < days; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const key = d.toISOString().slice(0, 10);
      result[key] = this.getAvailableSlots(d, durationMinutes, externalTasks);
    }
    return result;
  }

  // ── Event Types ────────────────────────────────────────────────────────────
  getEventTypes() { return this.eventTypes; }
  getEventType(id) { return this.eventTypes.find(e => e.id === id) || null; }

  createEventType(data) {
    const et = {
      id: uid(),
      name: 'New Event',
      description: '',
      duration: 30,
      color: '#5352ed',
      type: 'one-on-one',
      location: 'google-meet',
      locationDetails: '',
      bookingSlug: uid().slice(0, 8),
      isActive: true,
      maxAttendees: 1,
      requiresConfirmation: false,
      questions: [],
      reminderMinutes: [60, 1440],
      teamMembers: [],
      price: null,
      createdAt: new Date().toISOString(),
      ...data,
    };
    this.eventTypes = [...this.eventTypes, et];
    this._save(KEYS.eventTypes, this.eventTypes);
    return et;
  }

  updateEventType(id, updates) {
    this.eventTypes = this.eventTypes.map(e => e.id === id ? { ...e, ...updates } : e);
    this._save(KEYS.eventTypes, this.eventTypes);
    return this.eventTypes.find(e => e.id === id);
  }

  deleteEventType(id) {
    this.eventTypes = this.eventTypes.filter(e => e.id !== id);
    this._save(KEYS.eventTypes, this.eventTypes);
  }

  toggleEventType(id) {
    const et = this.getEventType(id);
    if (!et) return;
    return this.updateEventType(id, { isActive: !et.isActive });
  }

  // ── Bookings ───────────────────────────────────────────────────────────────
  getBookings() { return this.bookings; }
  getBooking(id) { return this.bookings.find(b => b.id === id) || null; }

  getBookingsForEventType(eventTypeId) {
    return this.bookings.filter(b => b.eventTypeId === eventTypeId);
  }

  getUpcomingBookings(n = 10) {
    const now = new Date().toISOString();
    return this.bookings
      .filter(b => b.status !== 'cancelled' && b.scheduledStart > now)
      .sort((a, b) => a.scheduledStart.localeCompare(b.scheduledStart))
      .slice(0, n);
  }

  createBooking(data) {
    const et = this.getEventType(data.eventTypeId);
    if (!et) throw new Error('Event type not found');

    // Round-robin: pick least-booked team member
    let assignedTo = data.assignedTo || null;
    if (et.type === 'round-robin' && et.teamMembers?.length > 0) {
      const counts = {};
      et.teamMembers.forEach(m => {
        counts[m] = this.bookings.filter(b => b.assignedTo === m && b.status !== 'cancelled').length;
      });
      assignedTo = et.teamMembers.reduce((a, b) => (counts[a] || 0) <= (counts[b] || 0) ? a : b);
    }

    const booking = {
      id: uid(),
      eventTypeId: data.eventTypeId,
      eventTypeName: et.name,
      inviteeName: data.inviteeName || '',
      inviteeEmail: data.inviteeEmail || '',
      inviteeTimezone: data.inviteeTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      scheduledStart: data.scheduledStart,
      scheduledEnd: data.scheduledEnd,
      status: et.requiresConfirmation ? 'pending' : 'confirmed',
      meetingLink: data.meetingLink || '',
      location: et.location,
      notes: data.notes || '',
      answers: data.answers || {},
      assignedTo,
      createdAt: new Date().toISOString(),
      remindersSent: [],
    };

    this.bookings = [...this.bookings, booking];
    this._save(KEYS.bookings, this.bookings);
    return booking;
  }

  updateBookingStatus(id, status) {
    this.bookings = this.bookings.map(b => b.id === id ? { ...b, status } : b);
    this._save(KEYS.bookings, this.bookings);
    return this.bookings.find(b => b.id === id);
  }

  deleteBooking(id) {
    this.bookings = this.bookings.filter(b => b.id !== id);
    this._save(KEYS.bookings, this.bookings);
  }

  // ── Meeting Polls ──────────────────────────────────────────────────────────
  getPolls() { return this.polls; }
  getPoll(id) { return this.polls.find(p => p.id === id) || null; }

  createPoll(data) {
    const poll = {
      id: uid(),
      title: data.title || 'Meeting Poll',
      description: data.description || '',
      duration: data.duration || 60,
      createdBy: data.createdBy || '',
      proposedSlots: (data.proposedSlots || []).map(s => ({ ...s, id: s.id || uid(), votes: s.votes || [] })),
      participants: data.participants || [],
      status: 'open',
      confirmedSlotId: null,
      createdAt: new Date().toISOString(),
    };
    this.polls = [...this.polls, poll];
    this._save(KEYS.polls, this.polls);
    return poll;
  }

  votePoll(pollId, slotId, voterName, voterEmail) {
    this.polls = this.polls.map(p => {
      if (p.id !== pollId) return p;
      return {
        ...p,
        proposedSlots: p.proposedSlots.map(s => {
          if (s.id !== slotId) return s;
          const key = voterEmail || voterName;
          const alreadyVoted = s.votes.includes(key);
          return { ...s, votes: alreadyVoted ? s.votes.filter(v => v !== key) : [...s.votes, key] };
        }),
      };
    });
    this._save(KEYS.polls, this.polls);
  }

  confirmPollSlot(pollId, slotId) {
    this.polls = this.polls.map(p =>
      p.id === pollId ? { ...p, confirmedSlotId: slotId, status: 'confirmed' } : p
    );
    this._save(KEYS.polls, this.polls);
  }

  deletePoll(id) {
    this.polls = this.polls.filter(p => p.id !== id);
    this._save(KEYS.polls, this.polls);
  }

  // ── Booking link helpers ───────────────────────────────────────────────────
  getBookingUrl(etId, username) {
    const et = this.getEventType(etId);
    if (!et) return '';
    if (username) return `${window.location.origin}/book/${username}/${et.bookingSlug}`;
    return `${window.location.origin}/?book=${et.bookingSlug}`; // fallback when username not yet set
  }

  // ── Stats ──────────────────────────────────────────────────────────────────
  getStats() {
    const now = new Date();
    const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay());
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 7);

    const confirmed = this.bookings.filter(b => b.status === 'confirmed');
    const thisWeek = confirmed.filter(b => {
      const s = new Date(b.scheduledStart);
      return s >= weekStart && s < weekEnd;
    });
    const upcoming = this.bookings.filter(b =>
      b.status !== 'cancelled' && new Date(b.scheduledStart) > now
    );

    return {
      totalBookings: this.bookings.length,
      confirmedTotal: confirmed.length,
      thisWeek: thisWeek.length,
      upcoming: upcoming.length,
      activeEventTypes: this.eventTypes.filter(e => e.isActive).length,
      openPolls: this.polls.filter(p => p.status === 'open').length,
    };
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────
const schedulingManager = new SchedulingManager();
export default schedulingManager;
export { DAYS };
